import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { HiX } from "react-icons/hi";

function EditTask({ task, onClose, getAllTasks, id }) {
  const [loading, setLoading] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [allSites, setAllSites] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [period, setPeriod] = useState("AM");
  const [isSubmitting, setIsSubmitting] = useState(false);

  //Initializing react-hook-form
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!task?.time) {
      setHours("");
      setMinutes("");
      setPeriod("");
      return;
    }
    const [time, session] = task?.time?.split(" ");
    const [hour, minute] = time?.split(":");
    setHours(hour);
    setMinutes(minute);
    setPeriod(session);
  }, [task]);

  //Setting the existing data
  useEffect(() => {
    reset({
      ...task,
      startDate: task.startDate ? task.startDate.split("T")[0] : "",
      site: task.site ? task.site._id : "",
      category: task.category ? task.category._id : "",
    });
  }, [task, allSites]);

  //Function to get all the sites
  const getAllSites = () => {
    setLoadingSites(true);
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/site/get-all-sites`,
      headers: {},
    };

    axios
      .request(config)
      .then((response) => {
        console.log("Response of getAllSites: ", response.data);
        setAllSites(response.data.sites);
        setLoadingSites(false);
      })
      .catch((error) => {
        console.log("Error in fetching all sites: ", error);
        setLoadingSites(false);
      });
  };

  //Function to get all the categories
  const getAllCategories = () => {
    setLoadingCategories(true);
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/category/get-all-categories`,
      headers: {},
    };

    axios
      .request(config)
      .then((response) => {
        console.log("Response of getAllCategories: ", response.data);
        setAllCategories(response.data.categories);
        setLoadingCategories(false);
      })
      .catch((error) => {
        console.log("Error in fetching all categories: ", error);
        setLoadingCategories(false);
      });
  };

  useEffect(() => {
    getAllSites();
    getAllCategories();
    getAllSites();
    getAllCategories();
  }, []);

  useEffect(() => {
    if (!loadingSites && !loadingCategories) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [loadingCategories, loadingSites]);

  const SkeletonLoader = () => (
    <div className="space-y-4">
      <div className="mx-auto h-8 w-3/4 animate-pulse rounded bg-gray-200"></div>

      <div className="my-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Input field skeletons - 8 fields in total with different heights */}
        {[...Array(8)].map((_, index) => (
          <div
            key={index}
            className={`flex flex-col gap-2 ${
              index >= 6 ? "col-span-2" : "col-span-2 md:col-span-1"
            }`}
          >
            <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
            <div className="h-10 w-full animate-pulse rounded bg-gray-200"></div>
          </div>
        ))}

        {/* Status skeleton */}
        <div className="col-span-2 flex flex-col gap-2">
          <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
          <div className="flex gap-4">
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 animate-pulse rounded-full bg-gray-200"></div>
              <div className="h-4 w-16 animate-pulse rounded bg-gray-200"></div>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 animate-pulse rounded-full bg-gray-200"></div>
              <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
            </div>
          </div>
        </div>

        {/* Button skeletons */}
        <div className="hidden md:block">
          <div className="mt-4 h-10 w-full animate-pulse rounded bg-gray-200"></div>
        </div>
        <div className="hidden md:block">
          <div className="mt-4 h-10 w-full animate-pulse rounded bg-gray-200"></div>
        </div>

        {/* Mobile buttons */}
        <div className="col-span-2 flex items-center justify-between gap-2 md:hidden">
          <div className="mt-4 h-10 w-1/2 animate-pulse rounded bg-gray-200"></div>
          <div className="mt-4 h-10 w-1/2 animate-pulse rounded bg-gray-200"></div>
        </div>
      </div>
    </div>
  );

  const onSubmit = (data) => {
    setIsSubmitting(true);

    const finalData = {
      ...data,
      time:
        hours && minutes
          ? `${hours}:${minutes.toString().padStart(2, "0")} ${period}`
          : null,
      userID: id,
    };

    let config = {
      method: "put",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/task/update-task/${task._id}`,
      headers: {
        "Content-Type": "application/json",
      },
      data: finalData,
    };

    axios
      .request(config)
      .then((response) => {
        console.log("Response of onSubmit", response.data);
        onClose();
        setIsSubmitting(false);
        getAllTasks();
        toast.success(response.data.message);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Error in editing task");
        setIsSubmitting(false);
      });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm backdrop-brightness-75 "
      onClick={onClose}
    >
      <div
        className="relative h-[80%] max-h-[95%] w-[95%] max-w-3xl overflow-y-auto rounded-lg bg-white p-4 shadow-lg md:p-8 lg:h-auto lg:px-6 lg:py-8"
        onClick={(e) => e.stopPropagation()}
      >
        <HiX
          className="absolute right-4 top-4 cursor-pointer text-primary hover:text-red-500"
          size={20}
          onClick={onClose}
        />
        {loading ? (
          <SkeletonLoader />
        ) : (
          <>
            <h1 className="text-center text-3xl font-bold tracking-wide text-primary">
              Edit Task
            </h1>

            {/* Input Fields */}
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="my-4 grid grid-cols-1 gap-4 md:grid-cols-2"
            >
              {/* Name */}
              <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
                <label
                  htmlFor="name"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Name <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  disabled={loading}
                  placeholder="Enter Name"
                  {...register("name", { required: "Name is required" })}
                  className={`rounded border bg-transparent px-4 py-2 outline-none focus:ring-1 focus:ring-primary ${
                    errors.name ? "border-red-500" : "border-gray-800"
                  }}`}
                />

                {errors.name && (
                  <span className="text-sm text-red-500">
                    {errors.name.message}
                  </span>
                )}
              </div>

              {/* Phone */}
              <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
                <label
                  htmlFor="phone"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Customer's Phone No <span className="text-red-500">*</span>
                </label>

                <input
                  type="tel"
                  placeholder="Enter Phone No"
                  {...register("phone", {
                    required: "Customer's Phone No is required",
                  })}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/[^0-9]/g, "")
                      .slice(0, 10);
                  }}
                  className={`rounded border bg-transparent px-4 py-2 outline-none focus:ring-1 focus:ring-primary ${
                    errors.phone ? "border-red-500" : "border-gray-800"
                  }}`}
                />

                {errors.phone && (
                  <span className="text-sm text-red-500">
                    {errors.phone.message}
                  </span>
                )}
              </div>

              {/* Location */}
              <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
                <label
                  htmlFor="location"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Location <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  disabled={loading}
                  placeholder="Enter Location"
                  {...register("location", {
                    required: "Location is required",
                  })}
                  className={`rounded border bg-transparent px-4 py-2 outline-none focus:ring-1 focus:ring-primary ${
                    errors.location ? "border-red-500" : "border-gray-800"
                  }}`}
                />

                {errors.location && (
                  <span className="text-sm text-red-500">
                    {errors.location.message}
                  </span>
                )}
              </div>

              {/* Site */}
              <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
                <label
                  htmlFor="site"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Select Site <span className="text-red-500">*</span>
                </label>

                <select
                  name="site"
                  id="site"
                  {...register("site", { required: "Site is required" })}
                  className={`rounded border bg-transparent px-4 py-2 outline-none focus:ring-1 focus:ring-primary ${
                    errors.site ? "border-red-500" : "border-gray-800"
                  }}`}
                >
                  <option value="" selected>
                    Select Site
                  </option>
                  {allSites?.map((site, index) => (
                    <option key={index} value={site._id}>
                      {site?.siteName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
                <label
                  htmlFor="category"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Category <span className="text-red-500">*</span>
                </label>

                <select
                  name="category"
                  id="category"
                  {...register("category", {
                    required: "Category is required",
                  })}
                  className={`rounded border bg-transparent px-4 py-2 outline-none focus:ring-1 focus:ring-primary ${
                    errors.category ? "border-red-500" : "border-gray-800"
                  }}`}
                >
                  <option value="" selected>
                    Select Category
                  </option>
                  {allCategories.length > 0 ? (
                    allCategories?.map((category, index) => (
                      <option value={category._id}>{category.name}</option>
                    ))
                  ) : (
                    <option value="">No categories available</option>
                  )}
                </select>

                {errors.category && (
                  <span className="text-sm text-red-500">
                    {errors.category.message}
                  </span>
                )}
              </div>

              {/* Priority */}
              <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
                <label
                  htmlFor="priority"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Priority <span className="text-red-500">*</span>
                </label>

                <select
                  name="priority"
                  id="priority"
                  disabled={loading}
                  {...register("priority", {
                    required: "Priority is required",
                  })}
                  className={`rounded border bg-transparent px-4 py-2 outline-none focus:ring-1 focus:ring-primary ${
                    errors.priority ? "border-red-500" : "border-gray-800"
                  }}`}
                >
                  <option value="" selected>
                    Select Priority
                  </option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>

                {errors.priority && (
                  <span className="text-sm text-red-500">
                    {errors.priority.message}
                  </span>
                )}
              </div>

              {/* Start Date */}
              <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
                <label
                  htmlFor="startDate"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Start Date <span className="text-red-500">*</span>
                </label>

                <input
                  type="date"
                  disabled={loading}
                  {...register("startDate", {
                    required: "Start Date is required",
                  })}
                  className={`rounded border bg-transparent px-4 py-2 outline-none focus:ring-1 focus:ring-primary ${
                    errors.startDate ? "border-red-500" : "border-gray-800"
                  }}`}
                />

                {errors.startDate && (
                  <span className="text-sm text-red-500">
                    {errors.startDate.message}
                  </span>
                )}
              </div>

              {/* Preferred Time  */}
              <div className="col-span-2 flex flex-col gap-2 md:col-span-1">
                <label
                  htmlFor="preferredTime"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Preferred Time
                </label>

                {/* Time Picker Container */}
                <div className="flex items-center gap-2">
                  {/* Hour Dropdown */}
                  <select
                    className="w-1/4 rounded-md border border-gray-200 bg-transparent px-3 py-2 text-center outline-none focus:border-none focus:ring-1 focus:ring-primary"
                    id="hour"
                    onChange={(e) => {
                      setHours(e.target.value);
                    }}
                    value={hours}
                  >
                    <option value="">Hour</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                      <option key={h} value={h.toString().padStart(2, "0")}>
                        {h}
                      </option>
                    ))}
                  </select>

                  <span className="text-primaryText">:</span>

                  {/* Minute Dropdown */}
                  <select
                    className="w-1/4 rounded-md border border-gray-200 bg-transparent px-3 py-2 text-center outline-none focus:border-none focus:ring-1 focus:ring-primary"
                    id="minute"
                    onChange={(e) => {
                      setMinutes(e.target.value);
                    }}
                    value={minutes}
                  >
                    <option value="">Min</option>
                    {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                      <option key={m} value={m.toString().padStart(2, "0")}>
                        {m.toString().padStart(2, "0")}
                      </option>
                    ))}
                  </select>

                  {/* AM/PM Dropdown */}
                  <select
                    className="w-1/4 rounded-md border border-gray-200 bg-transparent px-3 py-2 text-center outline-none focus:border-none focus:ring-1 focus:ring-primary"
                    id="period"
                    value={period || "AM"}
                    onChange={(e) => setPeriod(e.target.value)}
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="col-span-2 flex flex-col gap-2">
                <label
                  htmlFor="description"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Description <span className="text-red-500">*</span>
                </label>

                <textarea
                  type="text"
                  disabled={loading}
                  placeholder="Enter Description"
                  {...register("description", {
                    required: "Description is required",
                  })}
                  className={`resize-none rounded border bg-transparent px-4 py-2 outline-none focus:ring-1 focus:ring-primary ${
                    errors.description ? "border-red-500" : "border-gray-800"
                  }}`}
                />

                {errors.description && (
                  <span className="text-sm text-red-500">
                    {errors.description.message}
                  </span>
                )}
              </div>

              {/* Status */}
              <div className="col-span-2 flex flex-col gap-2 ">
                <label
                  htmlFor="status"
                  className="text-sm font-semibold tracking-wide text-primaryText"
                >
                  Status
                </label>
                <div className="flex gap-4">
                  <div className="flex items-center gap-1">
                    <input
                      type="radio"
                      disabled={loading}
                      checked={watch("status") === "Pending"}
                      value="Pending"
                      {...register("status")}
                      onChange={(e) => reset({ status: e.target.value })}
                    />
                    <label
                      htmlFor="status"
                      className="text-md font-semibold tracking-wide text-primaryText"
                    >
                      Pending
                    </label>
                  </div>

                  <div className="flex items-center gap-1">
                    <input
                      type="radio"
                      disabled={loading}
                      checked={watch("status") === "Completed"}
                      value="Completed"
                      {...register("status")}
                      onChange={(e) => reset({ status: e.target.value })}
                    />
                    <label
                      htmlFor="status"
                      className="text-md font-semibold tracking-wide text-primaryText"
                    >
                      Completed
                    </label>
                  </div>
                </div>

                {errors.location && (
                  <span className="text-sm text-red-500">
                    {errors.location.message}
                  </span>
                )}
              </div>

              {/* Buttons */}
              <button
                type="button"
                disabled={loading}
                className="mt-4 hidden w-1/2 rounded bg-gray-200 py-2 font-semibold tracking-wide text-gray-800 shadow-md hover:bg-gray-400 md:block md:w-full "
                onClick={() => {
                  reset();
                  setHours();
                  setMinutes();
                }}
              >
                Clear
              </button>

              <button
                type="submit"
                disabled={loading || isSubmitting}
                className=" mt-4 hidden w-1/2 items-center justify-center  rounded bg-primary py-2 font-semibold tracking-wide text-white shadow-md hover:bg-hoverPrimary md:flex md:w-full"
              >
                {isSubmitting ? <div className="loaderButton"></div> : "Submit"}
              </button>

              <div className="flex items-center justify-between gap-2 md:hidden">
                <button
                  type="button"
                  className="mt-4 w-1/2 rounded bg-gray-200 py-2 font-semibold tracking-wide text-gray-800 shadow-md hover:bg-gray-400  md:w-full "
                  onClick={() => {
                    reset();
                    setHours("");
                    setMinutes("");
                  }}
                >
                  Clear
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting || loading}
                  className=" mt-4 flex w-1/2 items-center  justify-center rounded bg-primary py-2 font-semibold tracking-wide text-white shadow-md hover:bg-hoverPrimary md:w-full"
                >
                  {isSubmitting ? (
                    <div className="loaderButton"></div>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default EditTask;
